import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';

import { OrderTypeEnum } from '../../models/orders-type.model';
import { DisplayDetailOrder, GetOrders, SetOrderType } from '../../store/orders-type.actions';
import {
    selectHasMore,
    selectOrdersOnline, selectOrdersStore,
    selectOrderType,
} from '../../store/orders-type.selectors';

import { Order } from '@api/order/order.models';
import { Resolutions } from '@core/domain/state/core.state';
import { getCmsConfigValue, getResolution } from '@core/infrastructure/state/core.selectors';
import { PaginationOptions } from '@pages/my-account/sections/returns/models/returns.models';
import { GoTo, GoToInternalPage } from '@shared/navigation/store/navigation.actions';
import { NavigationService } from '@shared/services/navigation/navigation.service';

@Component({
    selector: 'orders-type-layout',
    templateUrl: './orders-type-layout.component.html',
    styleUrls: ['./orders-type-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersTypeLayoutComponent implements OnInit {
    public readonly OrderTypeEnum = OrderTypeEnum;

    public readonly paginationOptions: PaginationOptions = {
        throttle: 50,
        scrollDistance: 4,
        scrollUpDistance: 4,
    };

    public readonly isMobile$: Observable<boolean> = this.store
        .select(getResolution)
        .pipe(map((resolutions: Resolutions) => resolutions === Resolutions.MOBILE));

    // eslint-disable-next-line no-secrets/no-secrets
    public readonly showVideoRefund$ = this.store.select(getCmsConfigValue('show_myAccount_refund_video'));

    public returnsVideoUrl$ = this.navigationService.returnsVideoUrl$;

    public readonly type$: Observable<OrderTypeEnum> = this.store.select(selectOrderType);

    public readonly ordersStore$: Observable<Array<Partial<Order>>> = this.store.select(selectOrdersStore);

    public readonly ordersOnline$: Observable<Array<Partial<Order>>> = this.store.select(selectOrdersOnline);

    public readonly hasMore$: Observable<boolean> = this.store.select(selectHasMore);

    public readonly showMultiDevo$: Observable<boolean> = this.store.select(getCmsConfigValue('devos_multipedido'));

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly store: Store,
        private readonly navigationService: NavigationService,
    ) {}

    public ngOnInit() {
        const orderType = this.activatedRoute.snapshot.data.type;

        const orderId = this.activatedRoute.snapshot.paramMap.get('id');

        if (orderId) {
            this.getOrderById(orderId);
        }

        this.store.dispatch(SetOrderType({ orderType }));

        this.store.dispatch(GetOrders());
    }

    public handleScroll() {
        this.store.dispatch(GetOrders());
    }

    public goToReturnsVideo() {
        this.returnsVideoUrl$.pipe(take(1)).subscribe((returnsVideoUrl) => {
            this.store.dispatch(GoTo({ url: returnsVideoUrl, newWindow: true }));
        });
    }

    public goReturn() {
        this.store.dispatch(GoToInternalPage({ url: `my-account/returns/return` }));
    }

    public getOrderById(orderId: string): void {
        this.store.select(selectOrdersOnline).pipe(
            filter(orders => !!orders),
            map(orders => orders.find(order => +order.id === +orderId)),
            filter(order => !!order),
            take(1),
            delay(500)
        ).subscribe(order => {
            this.openOrderDetail(order);
        });
    }

    public openOrderDetail(order: Partial<Order>): void {
        this.store.dispatch(DisplayDetailOrder({ order: order, reload: false }));
    }
}
